import { analyticsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  ArmAssignmentProductFilters,
  ArmAssignmentProductsResponse,
  ArmAssignmentReport,
  ArmAssignmentReportResult,
} from '../types/arm-assignment-report.types';

/** Opt Ky back into retry for imperative (non-Query) blob downloads — the client disables retry globally. */
const DOWNLOAD_RETRY = { limit: 2, methods: ['get'], statusCodes: [408, 429, 500, 502, 503, 504] };

/**
 * Fetch the per-arm assignment report. A 422 means the assignment isn't yet
 * computable (still building) — surfaced as `{ status: 'computing' }` so the
 * polling hook keeps waiting rather than failing; any other error rethrows.
 */
export async function fetchArmAssignmentReport(expId: string): Promise<ArmAssignmentReportResult> {
  try {
    const report = await analyticsClient
      .get(endpoints.analytics.experiments.armAssignmentReport(expId))
      .json<ArmAssignmentReport>();
    return { status: 'ready', report };
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 422) {
      return { status: 'computing' };
    }
    throw err;
  }
}

/**
 * Fetch a server-paginated page of assigned products. All narrowing is
 * server-side: `arm` (repeatable) + `segment` (repeatable) + `outliers` +
 * `search`, paged via `page` (1-based) and `page_size`.
 */
export async function fetchArmAssignmentProducts(
  expId: string,
  filters: ArmAssignmentProductFilters,
): Promise<ArmAssignmentProductsResponse> {
  const searchParams = new URLSearchParams();
  for (const arm of filters.arm ?? []) searchParams.append('arm', arm);
  for (const segment of filters.segment ?? []) searchParams.append('segment', segment);
  if (filters.outliers !== undefined) searchParams.append('outliers', String(filters.outliers));
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.page !== undefined) searchParams.append('page', String(filters.page));
  if (filters.pageSize !== undefined) searchParams.append('page_size', String(filters.pageSize));
  return analyticsClient
    .get(endpoints.analytics.experiments.armAssignmentProducts(expId), { searchParams })
    .json<ArmAssignmentProductsResponse>();
}

/**
 * Download the full test/control split as CSV via an authenticated blob fetch.
 * The server's `Content-Disposition` names the file; falls back to a local name.
 */
export async function downloadArmAssignmentCsv(expId: string): Promise<void> {
  const response = await analyticsClient.get(
    endpoints.analytics.experiments.armAssignmentExportCsv(expId),
    { retry: DOWNLOAD_RETRY },
  );
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${expId}-test-control-split.csv`;
  triggerBrowserDownload(blob, filename);
}

/**
 * Download the all-metrics per-arm CSV — every metric an arm carries, one row per
 * (arm, metric), each compared against the base arm. `subsidiaryName` is passed only
 * to name the file server-side; without it the server falls back to the tenant id.
 */
export async function downloadArmAssignmentAllMetricsCsv(
  expId: string,
  subsidiaryName?: string,
): Promise<void> {
  const searchParams = new URLSearchParams();
  if (subsidiaryName) searchParams.append('subsidiary_name', subsidiaryName);
  const response = await analyticsClient.get(
    endpoints.analytics.experiments.armAssignmentAllMetricsCsv(expId),
    { searchParams, retry: DOWNLOAD_RETRY },
  );
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${expId}-all-metrics.csv`;
  triggerBrowserDownload(blob, filename);
}

/** Download the customer-shareable Experiment Design recap (.docx) via an authenticated blob fetch. */
export async function downloadArmAssignmentDesignDoc(expId: string): Promise<void> {
  const response = await analyticsClient.get(
    endpoints.analytics.experiments.armAssignmentDesignDoc(expId),
    { retry: DOWNLOAD_RETRY },
  );
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${expId}-design-doc.docx`;
  triggerBrowserDownload(blob, filename);
}
